import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Shield, Cookie, Eye, EyeOff, Globe, AlertTriangle, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONSENT_STORAGE_KEY = 'aadc_privacy_consent';
const CONSENT_TIMESTAMP_KEY = 'aadc_consent_timestamp';
const GEO_CACHE_KEY = 'aadc_geo_cache';

// Consent expires after 12 months (GDPR requirement)
const CONSENT_EXPIRY_DAYS = 365;

/**
 * Privacy consent levels
 */
export const ConsentLevel = {
  NONE: 'none',           // No consent given yet - BLOCK ALL SCRIPTS
  ESSENTIAL: 'essential', // Only essential cookies (no tracking, no ads)
  LIMITED: 'limited',     // Non-personalized ads only
  FULL: 'full'            // Full personalized ads
};

/**
 * Script types that require consent
 */
const ScriptTypes = {
  ANALYTICS: 'analytics',   // PostHog, GA
  ADVERTISING: 'advertising', // AdSense, GAM
  SOCIAL: 'social'          // Social embeds
};

/**
 * Privacy Context - Now with script blocking
 */
const PrivacyContext = createContext({
  consentLevel: ConsentLevel.NONE,
  setConsentLevel: () => {},
  hasConsented: false,
  allowsPersonalizedAds: false,
  allowsAnyAds: false,
  allowsAnalytics: false,
  isEuUser: false,
  isLoading: true,
  revokeConsent: () => {},
  consentTimestamp: null,
  consentExpiry: null,
  scriptsLoaded: false
});

export const usePrivacy = () => useContext(PrivacyContext);

/**
 * Check if consent has expired (12 months)
 */
const isConsentExpired = () => {
  const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
  if (!timestamp) return true;
  
  const consentDate = new Date(parseInt(timestamp, 10));
  const expiryDate = new Date(consentDate);
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
  
  return new Date() > expiryDate;
};

/**
 * Load AdSense script (GDPR compliant)
 */
const loadAdSenseScript = (npa = '1') => {
  if (document.querySelector(`script[src*="adsbygoogle"]`)) return;
  
  const clientId = 'ca-pub-8607565924578094';
  const script = document.createElement('script');
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.dataset.consentScript = 'advertising';
  
  window.adsbygoogle = window.adsbygoogle || [];
  if (npa === '1') {
    window.adsbygoogle.requestNonPersonalizedAds = 1;
  }
  
  document.head.appendChild(script);
};

/**
 * Load PostHog analytics (GDPR compliant)
 */
const loadPostHogScript = () => {
  if (window.posthog?.__loaded) return;
  
  // PostHog is already initialized in index.html but we can control it
  if (window.posthog) {
    window.posthog.opt_in_capturing();
  }
};

/**
 * Disable PostHog tracking
 */
const disablePostHog = () => {
  if (window.posthog) {
    window.posthog.opt_out_capturing();
  }
};

/**
 * Remove all consent-dependent scripts
 */
const removeConsentScripts = () => {
  document.querySelectorAll('script[data-consent-script]').forEach(script => {
    script.remove();
  });
  
  // Clear AdSense
  if (window.adsbygoogle) {
    window.adsbygoogle.pauseAdRequests = 1;
  }
  
  // Opt out of PostHog
  disablePostHog();
};

/**
 * Privacy Provider - GDPR Compliant
 * Blocks ALL tracking/ad scripts until explicit consent is given
 */
export const PrivacyProvider = ({ children }) => {
  const [consentLevel, setConsentLevelState] = useState(ConsentLevel.NONE);
  const [isLoading, setIsLoading] = useState(true);
  const [isEuUser, setIsEuUser] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  // Detect EU users on mount
  useEffect(() => {
    const detectGeo = async () => {
      // Check cache first
      const cached = localStorage.getItem(GEO_CACHE_KEY);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          const cacheAge = Date.now() - data.timestamp;
          // Cache for 24 hours
          if (cacheAge < 24 * 60 * 60 * 1000) {
            setIsEuUser(data.isEu);
            return;
          }
        } catch (e) {
          // Ignore cache errors
        }
      }

      try {
        const response = await fetch(`${API_URL}/api/privacy/geo`);
        if (response.ok) {
          const data = await response.json();
          setIsEuUser(data.is_eu);
          localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({
            isEu: data.is_eu,
            countryCode: data.country_code,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        // Geo detection failed, default to strict mode
        // Default to strict mode if geo detection fails
        setIsEuUser(true);
      }
    };

    detectGeo();
  }, []);

  // Load saved consent on mount
  useEffect(() => {
    const loadConsent = () => {
      const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
      const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
      
      if (saved && !isConsentExpired()) {
        setConsentLevelState(saved);
        setConsentTimestamp(timestamp ? new Date(parseInt(timestamp, 10)) : null);
      } else if (saved && isConsentExpired()) {
        // Consent expired - reset to NONE and show banner again
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
        setConsentLevelState(ConsentLevel.NONE);
      }
      
      setIsLoading(false);
    };

    loadConsent();
  }, []);

  // Load/unload scripts based on consent level
  useEffect(() => {
    if (isLoading) return;

    // CRITICAL: Don't load any scripts until user has consented
    if (consentLevel === ConsentLevel.NONE) {
      removeConsentScripts();
      setScriptsLoaded(false);
      return;
    }

    if (consentLevel === ConsentLevel.ESSENTIAL) {
      // Essential only - remove all tracking/ad scripts
      removeConsentScripts();
      setScriptsLoaded(false);
      return;
    }

    // LIMITED or FULL consent - load appropriate scripts
    if (consentLevel === ConsentLevel.LIMITED) {
      // Non-personalized ads
      loadAdSenseScript('1');
      loadPostHogScript();
      setScriptsLoaded(true);
    } else if (consentLevel === ConsentLevel.FULL) {
      // Full personalized ads
      loadAdSenseScript('0');
      loadPostHogScript();
      setScriptsLoaded(true);
    }
  }, [consentLevel, isLoading]);

  // Update consent level and persist
  const updateConsent = useCallback(async (level) => {
    const now = Date.now();
    
    setConsentLevelState(level);
    setConsentTimestamp(new Date(now));
    
    localStorage.setItem(CONSENT_STORAGE_KEY, level);
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, now.toString());

    // Record consent on server for GDPR audit trail
    try {
      await fetch(`${API_URL}/api/privacy/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          consent_level: level,
          source: 'banner'
        })
      });
    } catch (error) {
      console.error('Failed to record consent:', error);
    }
  }, []);

  // Revoke consent
  const revokeConsent = useCallback(async () => {
    setConsentLevelState(ConsentLevel.ESSENTIAL);
    localStorage.setItem(CONSENT_STORAGE_KEY, ConsentLevel.ESSENTIAL);
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString());
    
    removeConsentScripts();
    setScriptsLoaded(false);

    // Record withdrawal on server
    try {
      await fetch(`${API_URL}/api/privacy/consent`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Failed to record consent withdrawal:', error);
    }
  }, []);

  const consentExpiry = consentTimestamp 
    ? new Date(consentTimestamp.getTime() + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    : null;

  const value = {
    consentLevel,
    setConsentLevel: updateConsent,
    hasConsented: consentLevel !== ConsentLevel.NONE,
    allowsPersonalizedAds: consentLevel === ConsentLevel.FULL,
    allowsAnyAds: consentLevel === ConsentLevel.LIMITED || consentLevel === ConsentLevel.FULL,
    allowsAnalytics: consentLevel === ConsentLevel.LIMITED || consentLevel === ConsentLevel.FULL,
    isEuUser,
    isLoading,
    revokeConsent,
    consentTimestamp,
    consentExpiry,
    scriptsLoaded
  };

  if (isLoading) return null;

  return (
    <PrivacyContext.Provider value={value}>
      {children}
    </PrivacyContext.Provider>
  );
};

/**
 * Cookie Consent Banner - GDPR Compliant
 * Shows for ALL users who haven't made a choice yet
 * EU users see additional warnings
 */
export const CookieConsentBanner = () => {
  const { consentLevel, setConsentLevel, hasConsented, isEuUser, isLoading } = usePrivacy();
  const [showDetails, setShowDetails] = useState(false);

  // Don't show if loading or already consented
  if (isLoading || hasConsented) return null;

  // EEA/UK/CH users see Google's IAB TCF v2.2-certified CMP (Funding Choices)
  // instead of our banner — enabled in AdSense → Privacy & messaging.
  // Google's CMP handles both ad-personalization consent (TCF signals) AND
  // the general cookie consent for these regions, so showing our banner too
  // would be redundant and could confuse users into double-consenting.
  if (isEuUser) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 bg-background/98 backdrop-blur-md border-t-2 border-primary/20 shadow-2xl"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-description"
    >
      <div className="container mx-auto max-w-4xl">
        {/* EU User Warning */}
        {isEuUser && (
          <div className="flex items-center gap-2 p-2 mb-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
            <Globe className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>EU Privacy Rights:</strong> Under GDPR, we need your explicit consent before using any tracking technologies.
            </span>
          </div>
        )}

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Shield className="w-7 h-7 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <h3 id="consent-title" className="font-semibold text-lg mb-1">Your Privacy Choices</h3>
              <p id="consent-description" className="text-sm text-muted-foreground">
                We respect your privacy. Choose how you want us to use cookies and tracking technologies. 
                {!isEuUser && ' We show ads to support our free community.'}
              </p>
              
              {/* Expandable Details */}
              <button 
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
              >
                {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showDetails ? 'Hide details' : 'What does each option mean?'}
              </button>
              
              {showDetails && (
                <div className="mt-3 text-sm space-y-2 p-3 bg-muted rounded-md">
                  <p className="flex items-start gap-2">
                    <EyeOff className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                    <span><strong>Essential Only:</strong> No ads, no tracking. Only cookies required for the site to work (login, preferences).</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <Shield className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                    <span><strong>Privacy-Friendly:</strong> Non-personalized ads with minimal analytics. No cross-site tracking.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <Cookie className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                    <span><strong>Personalized:</strong> Ads based on your interests using standard Google tracking.</span>
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConsentLevel(ConsentLevel.ESSENTIAL)}
              className="gap-1 flex-1 md:flex-none"
              data-testid="consent-essential-btn"
            >
              <EyeOff className="w-4 h-4" />
              Essential Only
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConsentLevel(ConsentLevel.LIMITED)}
              className="gap-1 flex-1 md:flex-none"
              data-testid="consent-limited-btn"
            >
              <Shield className="w-4 h-4" />
              Privacy-Friendly
            </Button>
            {!isEuUser && (
              <Button
                size="sm"
                onClick={() => setConsentLevel(ConsentLevel.FULL)}
                className="gap-1 flex-1 md:flex-none"
                data-testid="consent-full-btn"
              >
                <Cookie className="w-4 h-4" />
                Accept All
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <Link to="/privacy-policy" className="hover:underline flex items-center gap-1">
            <Settings className="w-3 h-3" />
            Privacy Policy & Cookie Details
          </Link>
          <span>Your choice is stored locally and expires in 12 months.</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Privacy Settings Toggle (for settings page)
 */
export const PrivacyToggle = ({ className = '' }) => {
  const { consentLevel, setConsentLevel, revokeConsent, consentTimestamp, consentExpiry, isEuUser } = usePrivacy();

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-medium flex items-center gap-2">
          <Shield className="w-4 h-4" />
          Privacy & Ad Settings
        </h4>
        {isEuUser && (
          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
            <Globe className="w-3 h-3" />
            EU User
          </span>
        )}
      </div>
      
      <div className="space-y-2">
        {[
          { 
            level: ConsentLevel.ESSENTIAL, 
            label: 'Essential Only', 
            desc: 'No ads, no tracking',
            icon: EyeOff,
            color: 'text-green-600'
          },
          { 
            level: ConsentLevel.LIMITED, 
            label: 'Privacy-Friendly', 
            desc: 'Non-personalized ads & basic analytics',
            icon: Shield,
            color: 'text-blue-600'
          },
          { 
            level: ConsentLevel.FULL, 
            label: 'Personalized', 
            desc: 'Personalized ads based on interests',
            icon: Cookie,
            color: 'text-amber-600',
            hidden: isEuUser
          }
        ].filter(opt => !opt.hidden).map(({ level, label, desc, icon: Icon, color }) => (
          <label 
            key={level}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              consentLevel === level ? 'border-primary bg-primary/5' : 'hover:bg-muted'
            }`}
          >
            <input
              type="radio"
              name="privacy"
              checked={consentLevel === level}
              onChange={() => setConsentLevel(level)}
              className="w-4 h-4"
            />
            <Icon className={`w-4 h-4 ${color}`} />
            <div className="flex-1">
              <div className="font-medium text-sm">{label}</div>
              <div className="text-xs text-muted-foreground">{desc}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Consent info */}
      <div className="text-xs text-muted-foreground p-3 bg-muted rounded-md space-y-1">
        <p><strong>Consent given:</strong> {formatDate(consentTimestamp)}</p>
        <p><strong>Expires:</strong> {formatDate(consentExpiry)}</p>
      </div>

      {/* Revoke button */}
      {consentLevel !== ConsentLevel.ESSENTIAL && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={revokeConsent}
          className="w-full gap-2"
        >
          <EyeOff className="w-4 h-4" />
          Revoke Consent (Essential Only)
        </Button>
      )}
    </div>
  );
};

/**
 * Get AdSense data attributes based on consent
 */
export const getAdSenseConfig = (consentLevel) => {
  switch (consentLevel) {
    case ConsentLevel.NONE:
    case ConsentLevel.ESSENTIAL:
      return { enabled: false };
    case ConsentLevel.LIMITED:
      return { 
        enabled: true, 
        npa: '1',  // Non-personalized ads
      };
    case ConsentLevel.FULL:
      return { 
        enabled: true,
        npa: '0'  // Personalized ads
      };
    default:
      return { enabled: false };
  }
};

/**
 * Script Blocker Component
 * Prevents loading of tracking scripts until consent is given
 * Place this early in your app to intercept script injections
 */
export const ScriptBlocker = () => {
  const { hasConsented, consentLevel } = usePrivacy();

  useEffect(() => {
    // If no consent or essential only, ensure scripts are blocked
    if (!hasConsented || consentLevel === ConsentLevel.ESSENTIAL) {
      // Block AdSense
      if (window.adsbygoogle) {
        window.adsbygoogle.pauseAdRequests = 1;
      }
      
      // Opt out of PostHog
      if (window.posthog) {
        window.posthog.opt_out_capturing();
      }
    }
  }, [hasConsented, consentLevel]);

  return null;
};

export default CookieConsentBanner;
